/**
 * payments 域 Zod 共享契约 — ADM-PAY-S001~S003
 *
 * 设计：
 * - 支付页面 = admin/payment_pages 表（id + app_id + scenario + name + is_active + is_built_in）
 * - Paywall 配置 = app_payment_methods (page.scenario 绑定 scenario，每个 page × IAP/STRIPE 一行)
 *   通道闭集 IAP/STRIPE（PayPal 已移除，dev-docs PR#64）；与 app_payment_methods active CHECK 对齐
 * - 上下架 = is_active toggle
 */

import { z } from 'zod';
import { type SettlementChannel, SettlementChannelSchema } from './payment-methods';

/**
 * 支付场景闭集（= C 端 app_payment_methods.scenario 闭集）。
 *
 * payment_pages.scenario 经 upsertPaywall 直写 app_payment_methods.scenario，而 C 端 GET /payment/methods
 * 按同一闭集（Zod enum）返回 placement —— 故 scenario 必须取自本闭集，否则写出的 scenario 永不被 C 端返回（死配置）。
 * dev-docs #97（张培 review #6）：payment_pages admin-own，scenario 必取 scenario 闭集 + admin 侧加同一校验。
 * **必须与 server 镜像 `schema/shared/app-payment-methods.ts` 的 PaymentScenarioLiteral 保持一致**。
 */
export const PAYMENT_SCENARIOS = [
  'charge_center',
  // reading_pass：小时阅读卡发现面（SRV-READ-022，novel-app-server#561 起 C 端已放行该 DB 取值）。
  // 它同时是**内建页**（§3「固定页」列 ✅），不出现在运营可自建场景里。
  'reading_pass',
  // novel_purchase：书籍买断发现面（SRV-READ-027 / #681）。固定页、默认下架、不进 offer 活动子集。
  // C 端 GET /payment/products 的 ScenarioEnum 尚未收本值——admin 发现面先扩，serving query 归 C 端。
  'novel_purchase',
  'limited_offer',
  // new_user_offer：新人优惠页独立支付页 scenario（#142 PR142-002，从 popup scene 迁出为独立支付页）。
  'new_user_offer',
  'operational_popup',
] as const;
export const PaymentScenarioSchema = z.enum(PAYMENT_SCENARIOS);
export type PaymentScenario = z.infer<typeof PaymentScenarioSchema>;

/**
 * 固定页（§3「固定页」列 ✅）。建 App 预置、禁删、运营不得 POST 新建。
 * 顺序 = 预置插入序。create-enum 必须排除本集合，否则存量 App 在回填前会插出 is_built_in=false 的同 scenario 页。
 */
export const BUILT_IN_PAYMENT_SCENARIOS = [
  'charge_center',
  'new_user_offer',
  'reading_pass',
  'novel_purchase',
] as const;
export type BuiltInPaymentScenario = (typeof BUILT_IN_PAYMENT_SCENARIOS)[number];

export const OPERATOR_PAYMENT_SCENARIOS = PAYMENT_SCENARIOS.filter(
  (s): s is Exclude<PaymentScenario, BuiltInPaymentScenario> =>
    !(BUILT_IN_PAYMENT_SCENARIOS as readonly string[]).includes(s),
);
export const OperatorPaymentScenarioSchema = z.enum(
  OPERATOR_PAYMENT_SCENARIOS as [string, ...string[]],
);

export const CreatePaymentPageRequestSchema = z.object({
  app_id: z.string().uuid(),
  /** 取值 ∈ PAYMENT_SCENARIOS；固定页由 service 返 APP_CONFLICT（契约 create 冲突码），不在 Zod 层改成 VALIDATION_FAILED */
  scenario: PaymentScenarioSchema,
  name: z.string().min(1).max(128),
});
export type CreatePaymentPageRequest = z.infer<typeof CreatePaymentPageRequestSchema>;

export const UpdatePaymentPageRequestSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  is_active: z.boolean().optional(),
});
export type UpdatePaymentPageRequest = z.infer<typeof UpdatePaymentPageRequestSchema>;

export const UpsertPaywallRequestSchema = z
  .object({
    // 结算通道层（`03-数据字典/70-产品配置.md` §3 两层闭集）：`app_payment_methods` 按通道存行。
    // 取 `SettlementChannelSchema` 而非平行手写 —— 闭集变动时各写入口自动跟随。
    payment_method: SettlementChannelSchema,
    // A8 §7.5：必填=否。`null` = 清空该渠道配置（视为未配置、不参与 serving 候选）；渠道行本身保留；
    // 省略 = 不改动该字段。
    // 渠道**无启停开关**——`is_active` 已随 dev-docs #530 废除。
    adapty_placement_id: z.string().min(1).max(128).nullable().optional(),
    // 不收 sort_order（A8 §7.5「请求体闭集 = 三键」）：通道排序归 §7.8 策略成员，商品排序归 §7.9；
    // .strict() 让误传即 VALIDATION_FAILED，也不再有「省略 = 默认 0」把既有 display_order 改写成 0 的路径。
    /**
     * 乐观锁前置条件（非可写字段，A8 §7.5）：上次 GET 读到的**该 App 该 scenario 全部渠道行**的
     * `max(updated_at)` —— scenario 级令牌，不是本行的 `updated_at`。`operational_popup` 改值会连带
     * 同步其余已填值通道行，行级令牌在「一填一空」时会漏掉静默覆盖。服务端在 scenario 锁内取同口径
     * max 逐字比对，不等即 `RESOURCE_CONFLICT`（`details.reason='STALE_SNAPSHOT'`）；缺省 = 不校验。
     * 形态取 `toISOString()`（GET 下发即该形态）。
     */
    expected_updated_at: z.string().datetime().optional(),
  })
  // .strict()：旧客户端继续发 `is_active` / `sort_order` 必须 422 而非被静默剥离后返回成功（否则运营以为改动生效了）。
  .strict();
export type UpsertPaywallRequest = z.infer<typeof UpsertPaywallRequestSchema>;

export type PaymentPageResponse = {
  id: string;
  app_id: string;
  scenario: string;
  name: string;
  is_built_in: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type PaywallConfigResponse = {
  id: string;
  page_id: string;
  /**
   * 结算通道层（§3 两层闭集），与请求侧同源 —— 新增通道时这里自动跟随，不必再手改一处字面联合。
   * Web 消费方（`paywall-adapter.ts`）同样按 `SETTLEMENT_CHANNELS` 遍历渠道，不另维护手写子集。
   */
  payment_method: SettlementChannel;
  /** `null` = 该渠道未配置，不参与 serving 候选（A8 §7.5）。 */
  adapty_placement_id: string | null;
  /** ISO 8601（`toISOString()`）；客户端取全部行的最大值作 PUT `expected_updated_at`（A8 §7.5）。 */
  updated_at: string;
};

/**
 * 支付页 scenario 的一条活跃引用（ADM-PAY-S004 删除阻断项）。
 * kind 闭集与 DELETE 的 PAY_PAGE_SCENARIO_REFERENCED.details.references 完全一致，
 * 供删除前预检端点（GET /payment-pages/:id/references）与错误 details 共用同一形态。
 */
export type PaymentPageScenarioReference = {
  kind: 'payment_offer' | 'operational_popup_scene';
  id: string;
  label: string;
};

/** GET /api/v1/payment-pages/:id/references 响应 — 删除前引用预检（锁外只读快照，权威校验仍在 DELETE 事务内） */
export type PaymentPageReferencesResponse = {
  references: PaymentPageScenarioReference[];
};
